import React, { useEffect } from 'react';
import {
  Button,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Tooltip,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import type { MenuProps } from '@fluentui/react-components';
import {
  PenSparkleRegular,
} from './images/PenSparkleRegular';

const useInlineCopilotUIStyles = makeStyles({
  triggerButton: {
    '&:disabled': {
      cursor: 'default',
      '&:hover': {
        cursor: 'default',
      },
    },
  },
  triggeredButton: {
    color: tokens.colorNeutralForeground2BrandHover,
  },
  popOver: {
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
  },
  title: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightBase200,
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    width: '228px',
  },
  menuItem: {
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: 0,
    '& .fui-MenuItem__content>div': {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: 0,
      paddingRight: 0,
      fontSize: tokens.fontSizeBase300,
    },
  },
});

export interface IInlineCopilotOption {
  menuItem: React.ReactNode;
  onClick: () => void;
  key: string;
}

export interface IInlineCopilotUIProps {
  title: React.ReactNode;
  options: IInlineCopilotOption[];
  disableReason?: string;
  locale?: string;
  onInlineCopilotOpen?: () => void;
  menuButtonAriaLabel?: string;
}

export const InlineCopilotUI: React.FC<Omit<IInlineCopilotUIProps, 'locale'>> = React.memo(({
  title,
  options,
  disableReason,
  onInlineCopilotOpen,
  menuButtonAriaLabel,
}) => {
  const styles = useInlineCopilotUIStyles();
  const [open, setOpen] = React.useState(false);
  const onOpenChange: MenuProps['onOpenChange'] = (e, data) => {
    setOpen(data.open);
  };

  useEffect(() => {
    if (open && onInlineCopilotOpen) {
      onInlineCopilotOpen();
    }
  }, [onInlineCopilotOpen, open]);

  return (
    <Menu open={open} onOpenChange={onOpenChange}>
      <MenuTrigger>
        { disableReason
          ? (
            <Tooltip content={disableReason} relationship="label">
              <Button
                disabled
                className={mergeClasses(styles.triggerButton, 'trigger-button')}
                icon={<PenSparkleRegular fill="currentColor" />}
                size="small"
                appearance="transparent"
                aria-label={menuButtonAriaLabel}
              />
            </Tooltip>
          )
          : (
            <Button
              className={mergeClasses(open && styles.triggeredButton, styles.triggerButton, 'trigger-button')}
              icon={<PenSparkleRegular />}
              size="small"
              appearance="transparent"
              aria-label={menuButtonAriaLabel}
            />
          )}
      </MenuTrigger>
      <MenuPopover className={mergeClasses(styles.popOver, 'inline-copilot-menu')}>
        <MenuList>
          <p className={styles.title}>{title}</p>
          {options.map(({ menuItem, onClick, key }) => (
            <MenuItem key={key} onClick={onClick} className={styles.menuItem}>{menuItem}</MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
});

InlineCopilotUI.defaultProps = {
  title: _TL_('Microsoft Advertising can help you:'),
  disableReason: '',
};
